export type BlogBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }

export type BlogPost = {
  slug: string
  path: string
  title: string
  description: string
  publishedAt: string
  updatedLabel: string
  eyebrow: string
  hook: string
  learn: string[]
  body: BlogBlock[]
  related: { label: string; to: string }[]
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'what-is-perfect-predict',
    path: '/blog/what-is-perfect-predict',
    title: 'What is Perfect Predict? Prediction trading on Live MT5',
    description:
      'Learn what Perfect Predict is: structured prediction trading on Gold and Silver, trader-chosen BUY or SELL, Client Portal management, and MetaTrader 5 execution — with clear risk limits.',
    publishedAt: '2026-08-09',
    updatedLabel: 'Published 9 August 2026',
    eyebrow: 'Product guide',
    hook: 'Perfect Predict is a structured prediction trading platform: you choose market direction, pick a strategy profile with clear parameters, and place a prediction through the Client Portal. Execution connects through MetaTrader 5 (MT5) infrastructure — not a points-only game. This guide explains what the product is, what it is not, and what to read before you fund a Live account.',
    learn: [
      'What Perfect Predict is (and is not)',
      'How the prediction ticket relates to MT5',
      'Which markets and strategy profiles are available today',
      'How Demo vs Live differs at a high level',
      'What to read before you deposit real funds',
    ],
    body: [
      { type: 'h2', text: 'What Perfect Predict is' },
      {
        type: 'p',
        text: 'Perfect Predict offers structured prediction trading with predefined strategy parameters, trader-selected direction (BUY or SELL — the platform does not choose for you), day-to-day account management in the Client Portal, and trade execution connected through MetaTrader 5 infrastructure.',
      },
      {
        type: 'p',
        text: 'The public website explains the product. Accounts, funding, withdrawals, and support are handled in the secure Client Portal.',
      },
      { type: 'h2', text: 'What Perfect Predict is not' },
      {
        type: 'ul',
        items: [
          'Not a guarantee of profit, payout, or “safe” returns',
          'Not investment, tax, or personal financial advice',
          'Not a system that picks BUY/SELL for you',
          'Not a substitute for reading the Risk Disclosure and understanding loss of capital',
        ],
      },
      {
        type: 'p',
        text: 'Markets move. Predictions can lose. Strategy labels and illustrative examples describe structure — they do not promise outcomes.',
      },
      { type: 'h2', text: 'Markets and strategy profiles' },
      {
        type: 'ul',
        items: [
          'Live prediction markets currently include Gold (XAU/USD) and Silver (XAG/USD). Additional markets may be introduced over time.',
          'Strategy profiles such as SAFE, BALANCE, and AGGRESSIVE are risk/reward structures with predefined parameters. They help you see the shape of a prediction before you confirm — they do not guarantee results.',
        ],
      },
      { type: 'h2', text: 'How the prediction ticket relates to MT5' },
      {
        type: 'p',
        text: 'In the Client Portal, Launch MT5 opens an in-app Create Prediction flow. The ticket is intentionally simple: market context, strategy, stake, and direction (SELL / BUY), then confirmation tied to your MT5 account path.',
      },
      {
        type: 'p',
        text: 'You still operate on Live MT5 infrastructure for real-funds trading. The portal UX makes the prediction structure clearer; it does not remove market risk.',
      },
      { type: 'h2', text: 'Demo vs Live' },
      {
        type: 'ul',
        items: [
          'Demo: for learning the flow with virtual balance (no need to fund Demo to practice the interface).',
          'Live: real funds, real market outcomes. Only fund a Live account after you understand the product and the Risk Disclosure.',
        ],
      },
      {
        type: 'p',
        text: 'Exact deposit methods and confirmation steps are explained on Funding and Withdrawals. Always use the method shown in your portal at the time you pay.',
      },
      { type: 'h2', text: 'Getting started' },
      {
        type: 'ol',
        items: [
          'Create an account on Perfect Predict (Client Portal registration).',
          'Complete any verification or account steps the portal requires.',
          'Read the Risk Disclosure before depositing.',
          'If you want to practice first, explore Demo — then fund Live only with money you can afford to lose.',
          'Deposit using the supported method shown in the portal; wait until your Live balance reflects the credit.',
          'Use Launch MT5 / Create Prediction only when you understand the ticket fields and the risk of loss.',
        ],
      },
      { type: 'h2', text: 'Risks and limitations' },
      {
        type: 'p',
        text: 'Trading leveraged financial instruments and prediction trading involve a high level of risk and may not be suitable for everyone. You may lose some or all of the capital you deposit. Balances can go down as well as up.',
      },
      {
        type: 'p',
        text: 'Funding rails can involve delays, wrong-network mistakes, and confirmation waits — payment submitted is not always the same moment as Live balance credited.',
      },
      {
        type: 'p',
        text: 'Strategy parameters and any payout illustrations on the website are examples only and do not guarantee trading outcomes. Past results do not predict future results.',
      },
      {
        type: 'p',
        text: 'This article is educational product information only. It is not personal investment, legal, tax, or financial advice. Perfect Predict does not guarantee profits or protection from loss. Read the Risk Disclosure before opening or funding a Live Trading Account.',
      },
      { type: 'h2', text: 'How Perfect Predict fits' },
      {
        type: 'ul',
        items: [
          'Public website for product information; Client Portal for accounts and trading actions',
          'Structured prediction ticket with trader-chosen direction',
          'Predefined strategy profiles (structure, not outcome guarantees)',
          'Gold and Silver markets currently available for Live predictions',
          'MT5 execution infrastructure behind the portal trade flow',
          'Demo for learning; Live for real funds',
        ],
      },
      { type: 'h2', text: 'Next step' },
      {
        type: 'p',
        text: 'If you want to explore Perfect Predict, create an account and read the Risk Disclosure first. Start with Demo if you need the interface, fund Live only with capital you can afford to lose, verify your first deposit credited correctly, then place a prediction only when you understand the ticket.',
      },
    ],
    related: [
      { label: 'About Perfect Predict', to: '/about' },
      { label: 'How Perfect Predict works', to: '/how-perfect-predict-works' },
      { label: 'How Live deposits credit', to: '/blog/how-live-deposits-credit-mt5-balance' },
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
      { label: 'Demo trading account', to: '/demo-trading-account' },
    ],
  },
  {
    slug: 'how-live-deposits-credit-mt5-balance',
    path: '/blog/how-live-deposits-credit-mt5-balance',
    title: 'How Live deposits credit your MT5 balance',
    description:
      'How Perfect Predict Live deposits work: Client Portal funding via Uniwire, payment confirmation vs Live balance credit, common mistakes, and what to check before your first prediction.',
    publishedAt: '2026-08-09',
    updatedLabel: 'Published 9 August 2026',
    eyebrow: 'Funding guide',
    hook: 'Funding a Live account is a sequence: you start a deposit in the Client Portal, pay with the exact details shown, wait for confirmation, then wait for Live balance credit. Those steps are related — they are not always the same instant. This guide sets expectations before you send funds.',
    learn: [
      'Where deposits start (Client Portal, not the marketing homepage)',
      'What the public site currently describes as the supported Live funding method',
      'What “payment confirmed” vs “Live balance credited” usually means',
      'Common mistakes that delay credit or send funds incorrectly',
      'What to do if credit is slower than expected',
      'Why you should never deposit more than you can afford to lose',
    ],
    body: [
      { type: 'h2', text: 'Where to deposit' },
      {
        type: 'p',
        text: 'Deposits for Live trading start inside the Perfect Predict Client Portal. The public website explains the product; funding actions happen after you sign in.',
      },
      {
        type: 'p',
        text: 'Use only the payment details shown in the portal for that deposit request. Do not reuse an old screenshot, a chat message, or details from someone else’s account.',
      },
      { type: 'h2', text: 'Supported funding method' },
      {
        type: 'p',
        text: 'On the public website, the currently described supported Live funding method is Uniwire, completed through the Client Portal. Demo accounts require no funding and receive a virtual opening balance.',
      },
      {
        type: 'p',
        text: 'If the portal UI ever shows additional options or extra fields for a specific request, follow what your account screen shows for that request — not an older article or third-party tip.',
      },
      { type: 'h2', text: 'The credit sequence' },
      {
        type: 'ol',
        items: [
          'Create or open a Live account path and complete any verification steps the portal requires.',
          'Start a Live deposit / funding request in the Client Portal.',
          'Pay using the exact details the portal displays for that request (supported method: Uniwire, as described publicly).',
          'Wait for the payment to be confirmed under provider / platform rules (timing varies).',
          'Wait for Perfect Predict to credit the Live trading balance after the payment is accepted.',
          'Only then treat funds as available for Create Prediction / Live trading on MT5 infrastructure.',
        ],
      },
      {
        type: 'p',
        text: '“I sent it” is not the same as “my Live equity updated.” Confirmation and credit are separate stages.',
      },
      { type: 'h2', text: 'What can slow credit' },
      {
        type: 'ul',
        items: [
          'Provider processing time or extra checks',
          'Incomplete verification still pending on the account',
          'Amount or reference that does not match the portal request',
          'Operational review when something looks unmatched',
          'Expecting instant credit equal to “payment submitted” status',
        ],
      },
      {
        type: 'p',
        text: 'There is no guaranteed fixed minute-count in this article. Watch the portal status for your request, and use Client Portal Support if credit does not appear after a reasonable wait.',
      },
      { type: 'h2', text: 'Common mistakes' },
      {
        type: 'ul',
        items: [
          'Paying outside the portal flow or to details that were not shown for this request',
          'Changing the amount after the request was created',
          'Reusing payment details from a previous deposit',
          'Funding before required verification is complete',
          'Treating Demo practice as if it were Live funding (Demo needs no deposit)',
          'Depositing money you cannot afford to lose',
        ],
      },
      { type: 'h2', text: 'Steps' },
      {
        type: 'ol',
        items: [
          'Sign in to the Client Portal.',
          'Open the Live funding / deposit flow.',
          'Confirm any required verification is complete.',
          'Follow the on-screen Uniwire / supported-method details for this request exactly.',
          'Keep your payment reference / confirmation evidence.',
          'Wait for portal status to show the payment accepted and Live balance credited.',
          'Verify the Live balance before placing a prediction.',
        ],
      },
      { type: 'h2', text: 'Risks and limitations' },
      {
        type: 'p',
        text: 'Trading and predictions involve risk of loss. Depositing does not create profit or protect you from loss. Funding rails can delay, fail, or be difficult to reverse if sent incorrectly.',
      },
      {
        type: 'p',
        text: 'This article is educational product information only — not financial, legal, tax, or investment advice. Perfect Predict does not guarantee profits or instant credit times. Never deposit funds you cannot afford to lose. Read the Risk Disclosure before funding a Live account.',
      },
      { type: 'h2', text: 'How Perfect Predict fits' },
      {
        type: 'ul',
        items: [
          'Live funding is portal-managed; the marketing site is informational',
          'Public site currently describes Uniwire as the supported Live funding method',
          'Demo needs no funding; Live uses real funds after credit',
          'Live balance must credit before real-funds predictions',
          'MT5 execution path uses Live funds after credit',
          'Account-specific deposit help belongs in Client Portal Support',
        ],
      },
      { type: 'h2', text: 'Next step' },
      {
        type: 'p',
        text: 'Ready to fund? Sign in, use only the deposit details shown in your portal, and read the Risk Disclosure first. Start with an amount you can afford to lose, then confirm credit before your first Live prediction.',
      },
    ],
    related: [
      { label: 'What is Perfect Predict?', to: '/blog/what-is-perfect-predict' },
      { label: 'Risk checklist before Live deposit', to: '/blog/risk-checklist-before-first-live-deposit' },
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
      { label: 'Demo trading account', to: '/demo-trading-account' },
      { label: 'About Perfect Predict', to: '/about' },
    ],
  },
  {
    slug: 'risk-checklist-before-first-live-deposit',
    path: '/blog/risk-checklist-before-first-live-deposit',
    title: 'Risk checklist before your first Live deposit',
    description:
      'A practical Perfect Predict checklist before your first Live deposit: Risk Disclosure, Demo vs Live, strategy structure vs guarantees, Uniwire portal funding, and what to verify after you pay.',
    publishedAt: '2026-08-11',
    updatedLabel: 'Published 11 August 2026',
    eyebrow: 'Risk guide',
    hook: 'A Live deposit turns practice into real capital at risk. Before you fund, pause and check that you understand the product, the prediction ticket, and the possibility of losing the money you send. Use this checklist as a personal gate — not as a promise that trading will be safe.',
    learn: [
      'A practical pre-deposit checklist you can run yourself',
      'What “structured prediction” still leaves to market risk',
      'Demo vs Live decision points',
      'What to do after you deposit (verify credit, then ticket)',
      'Where official risk wording lives on the Perfect Predict site',
      'What this checklist cannot do (it does not make trading low-risk)',
    ],
    body: [
      { type: 'h2', text: 'Why a checklist' },
      {
        type: 'p',
        text: 'Perfect Predict makes prediction structure clearer: predefined strategy parameters, trader-chosen BUY or SELL, and a Client Portal flow connected to MetaTrader 5 infrastructure. Clarity is not the same as low risk.',
      },
      {
        type: 'p',
        text: 'Leveraged and prediction trading can result in loss of some or all capital. Strategy labels and illustrative payout examples describe structure — they do not guarantee outcomes.',
      },
      { type: 'h2', text: 'Checklist before first Live deposit' },
      {
        type: 'ol',
        items: [
          'I have read the Risk Disclosure on Perfect Predict.',
          'I understand predictions can lose and nothing guarantees profit, payout, or target achievement.',
          'I know I choose BUY or SELL — the platform does not decide direction for me.',
          'I understand strategy profiles (for example SAFE / BALANCE / AGGRESSIVE) describe structure, not guaranteed results.',
          'I know which markets I intend to trade (currently Gold and Silver for Live predictions) and that markets have hours and quieter periods.',
          'I have tried Demo (recommended) so the Create Prediction ticket is familiar.',
          'I will deposit only money I can afford to lose.',
          'I will use only the deposit method and details shown in my Client Portal for this request (public site currently describes Uniwire for Live funding).',
          'I understand payment confirmation and Live balance credit can be separate steps — “I sent it” is not the same as “my Live equity updated.”',
          'I know where Client Portal Support is if something looks wrong after I pay.',
          'I am not relying on tips, screenshots from strangers, or “guaranteed” signal claims.',
          'I accept that past results and illustrations do not predict future results.',
        ],
      },
      {
        type: 'p',
        text: 'If any item is “no,” pause funding. Read the linked pages, stay on Demo longer, or ask Client Portal Support before you send real funds.',
      },
      { type: 'h2', text: 'After you deposit' },
      {
        type: 'ol',
        items: [
          'Keep your payment reference / confirmation evidence.',
          'Wait for the portal to show the payment accepted and the Live balance credited.',
          'Verify the Live balance before placing a prediction.',
          'Use Launch MT5 / Create Prediction only when you understand the ticket fields and the risk of loss.',
          'Start with an amount and frequency you can afford — never chase losses.',
        ],
      },
      { type: 'h2', text: 'What this checklist is not' },
      {
        type: 'ul',
        items: [
          'Not financial, legal, tax, or investment advice',
          'Not a substitute for the full Risk Disclosure page',
          'Not a claim that Perfect Predict is suitable for everyone',
          'Not a way to skip or soften legal risk notices on the website',
          'Not a guarantee of credit timing, profit, or capital protection',
        ],
      },
      { type: 'h2', text: 'Risks and limitations' },
      {
        type: 'p',
        text: 'Trading leveraged financial instruments involves a high level of risk and may not be suitable for all investors. You may lose some or all of your invested capital. Balances can go down as well as up.',
      },
      {
        type: 'p',
        text: 'Perfect Predict does not provide investment advice or guarantee profits. Strategy parameters and payout illustrations are examples only and do not guarantee any trading outcome. Funding rails can delay or fail if details are wrong.',
      },
      {
        type: 'p',
        text: 'This article is educational product information only. Read the Risk Disclosure before opening or funding a Live Trading Account.',
      },
      { type: 'h2', text: 'How Perfect Predict fits' },
      {
        type: 'ul',
        items: [
          'Official risk wording lives on site legal / risk pages — this blog post links to them; it does not replace them',
          'Client Portal holds Live funding, withdrawals, support, and Create Prediction',
          'Public website currently describes Uniwire as the supported Live funding method',
          'Structured ticket + MT5 infrastructure still means real market outcomes on Live',
          'Demo is for practice with virtual balance; Live uses real funds after credit',
        ],
      },
      { type: 'h2', text: 'Next step' },
      {
        type: 'p',
        text: 'Before your first Live deposit: open the Risk Disclosure, run this checklist, and use Demo if you still need the interface. When you fund, start small and only with capital you can afford to lose. Confirm credit before your first Live prediction.',
      },
    ],
    related: [
      { label: 'What is Perfect Predict?', to: '/blog/what-is-perfect-predict' },
      { label: 'How Live deposits credit', to: '/blog/how-live-deposits-credit-mt5-balance' },
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
      { label: 'Demo trading account', to: '/demo-trading-account' },
    ],
  },
]

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug)
}
